import { ErrorHandler, Injectable, NgZone, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { PosthogService } from './services/analytics/posthog.service';

@Injectable({ providedIn: 'root' })
export class PosthogErrorHandler implements ErrorHandler {
  private readonly ngZone = inject(NgZone);
  private readonly posthogService = inject(PosthogService);

  handleError(error: unknown): void {
    const extractedError = this.extractError(error) ?? 'Unknown error';

    this.ngZone.runOutsideAngular(() => {
      this.posthogService.posthog.captureException(extractedError);
    });
  }

  private extractError(errorCandidate: unknown): unknown {
    const error = this.unwrapZoneError(errorCandidate);

    if (error instanceof HttpErrorResponse) {
      return this.extractHttpError(error);
    }

    if (typeof error === 'string' || this.isErrorLike(error)) {
      return error;
    }

    return null;
  }

  private unwrapZoneError(error: unknown): unknown {
    if (error && typeof error === 'object' && 'ngOriginalError' in error) {
      return (error as { ngOriginalError: unknown }).ngOriginalError;
    }

    return error;
  }

  private extractHttpError(error: HttpErrorResponse): string | Error {
    if (this.isErrorLike(error.error)) {
      return error.error;
    }

    if (
      typeof ErrorEvent !== 'undefined' &&
      error.error instanceof ErrorEvent &&
      error.error.message
    ) {
      return error.error.message;
    }

    return error.message;
  }

  private isErrorLike(value: unknown): value is Error {
    if (value instanceof Error) {
      return true;
    }

    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
      return false;
    }

    return 'name' in value && 'message' in value && 'stack' in value;
  }
}
